import {
  SquaresFourIcon,
  CubeIcon,
  ShareNetworkIcon,
  ArrowsLeftRightIcon,
  PackageIcon,
  TruckIcon,
  BuildingsIcon,
  QrCodeIcon,
  ChartBarIcon,
  WarehouseIcon,
  ClipboardTextIcon,
  ShoppingCartIcon,
  ArrowsDownUpIcon,
  ClipboardIcon,
  CameraIcon,
  CodeIcon,
  ImageIcon,
  LightningIcon,
  BicycleIcon,
  CurrencyDollarIcon,
  UsersThreeIcon,
  MapTrifoldIcon,
} from '@phosphor-icons/react';
import { PATHS_NAMES } from '../pathnames';
import { IResource, Role } from '../types';

const WAREHOUSE_EXECUTIVE_AND_ABOVE: Array<Role> = [
  'WAREHOUSE_ADMIN',
  'WAREHOUSE_MANAGER',
  'WAREHOUSE_EXECUTIVE',
];
const WAREHOUSE_MANAGER_AND_ABOVE: Array<Role> = ['WAREHOUSE_ADMIN', 'WAREHOUSE_MANAGER'];
// Pickers reach the Picklists screen to work their own assigned picklists; the page itself
// scopes them to their own picklists (see PICKLIST_VIEW_ALL_ROLES in libs/pages picklist/pick.tsx).
const PICKLIST_ACCESS_ROLES: Array<Role> = [...WAREHOUSE_EXECUTIVE_AND_ABOVE, 'PICKER'];
const WAREHOUSE_LOGISTICS_ROLES: Array<Role> = [
  'RIDER',
  'RIDER_MANAGER',
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'SUPER_ADMIN',
];
const HUB_OPERATIONS_LOGISTICS_ROLES: Array<Role> = [
  'RIDER_MANAGER',
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'SUPER_ADMIN',
];
const RIDER_MANAGEMENT_ROLES: Array<Role> = HUB_OPERATIONS_LOGISTICS_ROLES;
const RIDER_MANAGEMENT_MUTATION_ROLES: Array<Role> = ['RIDER_MANAGER', 'SUPER_ADMIN'];
// Facility ops and rider managers arrange return pickups (including Book with Porter) in Hub.
const RETURN_OPERATIONS_ROLES: Array<Role> = [
  ...WAREHOUSE_EXECUTIVE_AND_ABOVE,
  'FACILITY_OPS',
  'RIDER_MANAGER',
];

export const getWarehouseResources = (isAllFacilities?: boolean): Array<IResource> => [
  {
    name: 'dashboard',
    list: '/',
    meta: {
      label: 'Dashboard',
      icon: <SquaresFourIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'packages',
    list: '/packages',
    show: '/packages/show/:id',
    meta: {
      label: 'Packages',
      icon: <CubeIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      permissions: { show: ['RIDER_MANAGER'], assignJitCourier: ['RIDER_MANAGER'] },
    },
  },
  {
    name: 'ordercontrolcenter',
    list: '/ordercontrolcenter',
    meta: {
      label: 'Control Center',
      icon: <ShareNetworkIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      permissions: { list: ['RIDER_MANAGER'] },
    },
  },
  {
    name: PATHS_NAMES.BULK_PICK,
    list: '/picklists',
    meta: {
      label: 'Picklists',
      icon: <LightningIcon />,
      masterRoles: PICKLIST_ACCESS_ROLES,
    },
  },
  {
    name: 'returns',
    list: '/returns',
    show: '/returns/show/:id',
    meta: {
      label: 'Returns',
      icon: <ArrowsLeftRightIcon />,
      masterRoles: RETURN_OPERATIONS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.RETURN_COURIER_EXCEPTIONS,
    list: '/returns/exceptions/cancelled-awb-movement',
    meta: {
      label: 'Return Courier Exceptions',
      icon: <ArrowsLeftRightIcon />,
      masterRoles: WAREHOUSE_MANAGER_AND_ABOVE,
    },
  },
  {
    name: 'refunds-onhold',
    list: '/refunds-onhold',
    show: '/refunds/show/:id',
    meta: {
      label: 'Refunds Onhold',
      icon: <CurrencyDollarIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'inventory',
    list: '/inventory',
    meta: {
      label: 'Inventory',
      icon: <PackageIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: PATHS_NAMES.CYCLE_COUNT,
    list: '/cycle-count',
    meta: {
      label: 'Cycle Count',
      icon: <ClipboardIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'vendormanagement',
    meta: {
      label: 'Vendors',
      icon: <TruckIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'vendors',
    meta: {
      parent: 'vendormanagement',
      label: 'Vendors',
      icon: <BuildingsIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
    list: '/vendors',
    show: '/vendors/show/:id',
  },
  {
    name: 'skus',
    list: '/skus',
    meta: {
      parent: 'vendormanagement',
      label: 'Vendor Catalog',
      icon: <QrCodeIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: PATHS_NAMES.SKU_ENQUIRY_HISTORY,
    list: '/vendors/sku-enquiry-history',
    meta: {
      parent: 'vendormanagement',
      label: 'SKU Enquiry History',
      icon: <ChartBarIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'inbound',
    meta: {
      label: 'Inbound',
      icon: <WarehouseIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'grn',
    list: '/grn',
    show: '/grn/show/:id',
    edit: '/grn/edit/:id',
    meta: {
      parent: 'inbound',
      label: 'GRN',
      icon: <ClipboardTextIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        show: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        edit: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        export_barcode: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        receive: WAREHOUSE_EXECUTIVE_AND_ABOVE,
        import: ['IMPORT_ADMIN'],
      },
    },
  },
  {
    name: 'mtopurchaseorders',
    list: '/mtopurchaseorders',
    meta: {
      parent: 'inbound',
      label: 'MTO Purchase Orders',
      icon: <ShoppingCartIcon />,
      permissions: {
        list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    // Permission-only (no route/menu): gates the DC facility-transfer action on the Hub DC show page.
    name: 'deliverychallans',
    meta: {
      permissions: {
        transfer_facility: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  ...(isAllFacilities
    ? [
        {
          name: PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
          list: '/incoming-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Stock Transfers',
            icon: <ArrowsDownUpIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]
    : [
        {
          name: PATHS_NAMES.INCOMING_STOCK_TRANSFERS,
          list: '/incoming-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Incoming Stock Transfers',
            icon: <ArrowsDownUpIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          name: PATHS_NAMES.OUTGOING_STOCK_TRANSFERS,
          list: '/outgoing-stock-transfers',
          meta: {
            parent: 'inbound',
            label: 'Outgoing Stock Transfers',
            icon: <TruckIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]),
  {
    // Permission-only detail route used from package Stock Transfer "View" links.
    name: PATHS_NAMES.STOCK_TRANSFER,
    edit: '/stocktransfer/edit/:id',
    meta: {
      permissions: {
        edit: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  ...(isAllFacilities
    ? []
    : [
        {
          name: PATHS_NAMES.INCOMING_MANIFESTS,
          list: '/incoming-manifests',
          meta: {
            parent: 'inbound',
            label: 'Incoming Manifests',
            icon: <ClipboardTextIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
        {
          name: PATHS_NAMES.OUTGOING_MANIFESTS,
          list: '/outgoing-manifests',
          meta: {
            parent: 'inbound',
            label: 'Outgoing Manifests',
            icon: <ClipboardIcon />,
            permissions: {
              list: WAREHOUSE_EXECUTIVE_AND_ABOVE,
            },
          },
        },
      ]),
  {
    name: PATHS_NAMES.MANIFESTS,
    create: '/manifests/create',
    show: '/manifests/:id/show',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.GATEPASS_ORDERS,
    show: '/gatepass-orders/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'packages',
    show: '/packages/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'lineitems',
    show: '/lineitems/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      permissions: {
        transfer_facility: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      },
    },
  },
  {
    name: 'orders',
    show: '/orders/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: 'refunds',
    show: '/refunds/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.FACILITIES,
    show: '/facilities/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.B2B_CUSTOMERS,
    show: '/b2b-customer/show/:id',
    meta: {
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  { name: 'skuupload' },
  { name: 'pincode' },
  {
    name: PATHS_NAMES.UTILITY,
    meta: {
      label: 'Utility',
      icon: <CodeIcon />,
      masterRoles: ['*'],
    },
  },
  {
    name: PATHS_NAMES.PRODUCT_IMAGE_SEARCH,
    list: '/utility/product-image-search',
    meta: {
      parent: PATHS_NAMES.UTILITY,
      label: 'Product Image Search',
      icon: <ImageIcon />,
      masterRoles: ['*'],
    },
  },
  {
    name: 'store-images',
    list: '/utility/store-images',
    meta: {
      parent: PATHS_NAMES.UTILITY,
      label: 'Store Images',
      icon: <CameraIcon />,
      masterRoles: ['*'],
    },
  },
  {
    name: PATHS_NAMES.SHELF_TRANSFER,
    list: '/utility/shelf-transfer',
    meta: {
      parent: PATHS_NAMES.UTILITY,
      label: 'Shelf Transfer',
      icon: <ArrowsDownUpIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
      // The workflow is a single list route; do not grant resource-wide wildcard actions.
      permissions: {
        list: ['SHELF_TRANSFER'],
      },
    },
  },
  {
    name: PATHS_NAMES.DASH_ORDERS,
    list: '/dash-orders',
    meta: {
      label: 'SELF Orders',
      icon: <LightningIcon />,
      masterRoles: WAREHOUSE_EXECUTIVE_AND_ABOVE,
    },
  },
  {
    name: PATHS_NAMES.LOGISTICS,
    list: '/logistics',
    meta: {
      label: 'Logistics',
      icon: <TruckIcon />,
      masterRoles: WAREHOUSE_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.MY_RIDES,
    list: '/logistics/my-rides',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'My Rides',
      icon: <BicycleIcon />,
      masterRoles: WAREHOUSE_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.OPEN_WORK_MAP,
    list: '/logistics/open-work-map',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Open Work Map',
      icon: <MapTrifoldIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.RIDER_MANAGEMENT,
    list: '/logistics/riders',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Rider Management',
      icon: <UsersThreeIcon />,
      masterRoles: RIDER_MANAGEMENT_ROLES,
      permissions: {
        list: RIDER_MANAGEMENT_ROLES,
        create: RIDER_MANAGEMENT_MUTATION_ROLES,
        edit: RIDER_MANAGEMENT_MUTATION_ROLES,
        delete: RIDER_MANAGEMENT_MUTATION_ROLES,
      },
    },
  },
  {
    name: PATHS_NAMES.CASH_HANDOVER,
    list: '/logistics/cash-handover',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Cash Handover',
      icon: <CurrencyDollarIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.RIDER_ATTENDANCE_QR,
    list: '/logistics/attendance-qr',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Attendance QR',
      icon: <QrCodeIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
  {
    name: PATHS_NAMES.RIDER_HEALTH,
    list: '/logistics/rider-health',
    meta: {
      parent: PATHS_NAMES.LOGISTICS,
      label: 'Rider Control Centre',
      icon: <BicycleIcon />,
      masterRoles: HUB_OPERATIONS_LOGISTICS_ROLES,
    },
  },
];

// Backward compatibility - keep the old static array
export const WAREHOUSE_RESOURCES: Array<IResource> = getWarehouseResources(false);
